import React from 'react';

import debounce from 'lodash/debounce';

import { REAL_SIZE_FOR_LUMX_SIZE, REAL_SIZE_FOR_LUMX_SPACING } from '@lumapps/lumx/constants';

type InheritedProps = React.ComponentPropsWithoutRef<'div'>;

export interface UseArrowSideScrollContainerProps extends InheritedProps {
    paddingVertical?: keyof typeof REAL_SIZE_FOR_LUMX_SPACING;
    paddingHorizontal?: keyof typeof REAL_SIZE_FOR_LUMX_SPACING;
}

const PADDING = 8;
const ARROW_WIDTH = REAL_SIZE_FOR_LUMX_SIZE.m;
const ARROW_GRADIENT_MARGIN = REAL_SIZE_FOR_LUMX_SIZE.s;
const ARROW_FULL_WIDTH = ARROW_WIDTH + ARROW_GRADIENT_MARGIN;

export const useArrowSideScrollContainer = ({
    paddingHorizontal,
    paddingVertical,
}: UseArrowSideScrollContainerProps) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [scrollZone, setScrollZone] = React.useState<HTMLDivElement | null>(null);
    const [showArrowLeft, setShowArrowLeft] = React.useState(false);
    const [showArrowRight, setShowArrowRight] = React.useState(false);

    React.useEffect(() => {
        if (!scrollZone) {
            return undefined;
        }

        const updateScrollState = debounce(
            () => {
                const hasScroll = scrollZone.scrollWidth > scrollZone.clientWidth;

                setShowArrowLeft(hasScroll && Math.floor(scrollZone.scrollLeft) > 0);
                setShowArrowRight(
                    hasScroll && Math.ceil(scrollZone.scrollLeft) + scrollZone.clientWidth < scrollZone.scrollWidth,
                );
            },
            100,
            { leading: true, trailing: true },
        );

        // Update scroll state on resize
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(scrollZone);

        // Update scroll state on children nodes updated
        const mutationObserver = new MutationObserver(updateScrollState);
        mutationObserver.observe(scrollZone, { childList: true, subtree: true });

        // Update scroll state on scroll
        scrollZone.addEventListener('scroll', updateScrollState);

        // Scroll focused element into view
        const scrollTargetIntoView = (event: FocusEvent) => {
            const target = event.target as HTMLElement;
            // Skip if focusing the scroll zone or ig not a direct child
            if (target === scrollZone || !scrollZone.contains(target)) {
                return;
            }

            // Scroll target item into view
            requestAnimationFrame(() => {
                target.scrollIntoView?.({ inline: 'start', block: 'nearest' });
            });
        };
        scrollZone.addEventListener('focus', scrollTargetIntoView, { capture: true });

        return () => {
            scrollZone.removeEventListener('focus', scrollTargetIntoView);
            scrollZone.removeEventListener('scroll', updateScrollState);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [scrollZone]);

    // Callback on arrow left/right click
    const [onScrollLeft, onScrollRight] = React.useMemo(() => {
        function scrollSide(direction: 1 | -1) {
            return () => {
                if (!scrollZone) {
                    return;
                }

                // Estimated scroll page width removing the paddings and arrows
                const scrollPageWidth = scrollZone.clientWidth - PADDING * 2 - ARROW_FULL_WIDTH * 2;
                const offsetLeft = direction * scrollPageWidth;
                const maxLeft = scrollZone.scrollWidth - scrollZone.clientWidth;

                // Scroll a page to the right/left
                scrollZone.scrollTo({
                    left: Math.min(scrollZone.scrollLeft + offsetLeft, maxLeft),
                });
            };
        }
        return [scrollSide(-1), scrollSide(1)];
    }, [scrollZone]);

    // Custom properties
    const style: any = {};
    if (paddingVertical) {
        style['--_padding-vertical'] = `${REAL_SIZE_FOR_LUMX_SPACING[paddingVertical]}px`;
    }
    if (paddingHorizontal) {
        style['--_padding-horizontal'] = `${REAL_SIZE_FOR_LUMX_SPACING[paddingHorizontal]}px`;
    }

    return {
        style,
        onScrollLeft,
        onScrollRight,
        setScrollZone,
        showArrowRight,
        showArrowLeft,
        ref,
    };
};
